import PageHero from "@/components/site/PageHero";
import SectionLabel from "@/components/site/SectionLabel";
import Seo from "@/components/site/Seo";
import { BRAND } from "@/lib/constants";

function LegalShell({ title, highlight, eyebrow, eyebrowNumber, children, pageTitle }) {
  return (
    <div>
      <Seo />
      <PageHero
        pageTitle={pageTitle}
        eyebrowNumber={eyebrowNumber}
        eyebrow={eyebrow}
        title={title}
        highlight={highlight}
      />
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14 font-manrope text-soft-gray leading-relaxed space-y-6">
          {children}
          <hr className="border-slate-soft mt-8" />
          <p className="font-manrope text-muted-ink text-sm">
            Last updated: February 2026 · {BRAND.producer} · {BRAND.city}
          </p>
        </div>
      </section>
    </div>
  );
}

function H({ children, n }) {
  return (
    <>
      <SectionLabel number={n}>{children}</SectionLabel>
    </>
  );
}

export function Privacy() {
  return (
    <LegalShell
      pageTitle="Privacy"
      eyebrowNumber="01"
      eyebrow="Privacy Policy"
      title="How We Treat"
      highlight="Your Information."
    >
      <H n="01">Overview</H>
      <p>
        {BRAND.producer} ("TNS Media Group", "we", "our") operates TheNextStageShow.com.
        This policy describes what information we collect, how we use it, and
        the choices you have. We treat the people who reach out to us with
        respect — the same way we treat the speakers on the stage.
      </p>
      <H n="02">What We Collect</H>
      <p>
        Information you voluntarily submit through our forms — name, email,
        phone, organization, and the content of your message. We also collect
        basic, non-identifying analytics about page visits to improve the site.
      </p>
      <H n="03">How We Use It</H>
      <p>
        Strictly to follow up with you regarding The Next Stage and to operate
        the platform. We do not sell, rent, or share your information with
        unrelated third parties. We use a transactional email provider to
        deliver our messages.
      </p>
      <H n="04">Your Choices</H>
      <p>
        You can request deletion or update of your information at any time by
        emailing <a href={`mailto:${BRAND.email}`} className="text-gold hover:text-gold-bright">{BRAND.email}</a>. You can opt out of future
        communication using the unsubscribe link in any email we send.
      </p>
      <H n="05">Contact</H>
      <p>
        Questions about this policy: {BRAND.email} · {BRAND.phone}.
      </p>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell
      pageTitle="Terms"
      eyebrowNumber="01"
      eyebrow="Terms Of Use"
      title="Using"
      highlight="This Site."
    >
      <H n="01">Acceptance</H>
      <p>
        By using TheNextStageShow.com, you agree to these terms. If you do not
        agree, please discontinue use of the site.
      </p>
      <H n="02">Content</H>
      <p>
        All site content, brand marks, and editorial copy are © {new Date().getFullYear()}{" "}
        {BRAND.producer}, unless otherwise noted. The Next Stage and related
        marks are used under the producer's authority.
      </p>
      <H n="03">User Submissions</H>
      <p>
        Information submitted through our forms is governed by our Privacy
        Policy. You represent that submissions are truthful and that you have
        the right to submit any information you provide.
      </p>
      <H n="04">No Warranties</H>
      <p>
        The site is provided "as is." We make no warranties about uninterrupted
        access, accuracy of forward-looking event details (which are marked "to
        be announced" where applicable), or external links.
      </p>
      <H n="05">Limitation Of Liability</H>
      <p>
        To the maximum extent permitted by law, {BRAND.producer} is not liable
        for indirect, incidental, or consequential damages arising from your
        use of the site.
      </p>
      <H n="06">Governing Law</H>
      <p>
        These terms are governed by the laws of the State of Colorado, without
        regard to conflict-of-law rules.
      </p>
    </LegalShell>
  );
}

export function Accessibility() {
  return (
    <LegalShell
      pageTitle="Accessibility"
      eyebrowNumber="01"
      eyebrow="Accessibility Statement"
      title="Built To Be"
      highlight="Reached."
    >
      <H n="01">Our Commitment</H>
      <p>
        We design and build TheNextStageShow.com to meet WCAG 2.1 AA standards.
        Public speaking is a platform for voices — and the platform's website
        should be reachable by every voice that wants to step forward.
      </p>
      <H n="02">What We Do</H>
      <p>
        Keyboard navigation, visible focus states, semantic HTML, alt text on
        meaningful images, and color choices tested against AA contrast on our
        dark theme. We avoid auto-playing motion and audio.
      </p>
      <H n="03">Where We Are Still Working</H>
      <p>
        Accessibility is ongoing. Where we fall short, we want to know.
        Reporting a barrier helps us fix it — quickly and seriously.
      </p>
      <H n="04">Tell Us</H>
      <p>
        Email <a href={`mailto:${BRAND.email}`} className="text-gold hover:text-gold-bright">{BRAND.email}</a> or call {BRAND.phone} with the page,
        the issue, and your assistive technology. We respond personally.
      </p>
      <H n="05">Event Accessibility</H>
      <p>
        We are committed to selecting accessible event venues for the pilot
        series. Specific accessibility detail will be published with each
        event.
      </p>
    </LegalShell>
  );
}
